/* De plaats van een projectverhaal, voor de kaart op de pagina Projecten van de site (ADR 0024):
 * de kolommen `place`, `latitude` en `longitude` op website.projects. Een project krijgt een stip
 * als alle drie zijn ingevuld (lib/projectkaart.ts in de site-repo).
 *
 * Het gaat om het midden van de plaats, nooit om het adres van de klant. Daarom houden de getallen
 * twee cijfers achter de komma: dat is ongeveer een kilometer. Wat fijner wordt ingevuld, wordt hier
 * al afgerond en gaat dus niet naar de database.
 *
 * De regels zijn die van de database (db/voorstel/2026-10-09-projectkaart.sql in de site-repo):
 *   - breedtegraad van 50,7 tot en met 53,6 en lengtegraad van 3,3 tot en met 7,3 (Nederland);
 *   - breedte en lengte samen, of geen van beide;
 *   - een plaatsnaam zonder getallen mag: die geeft nog geen stip.
 *
 * De kolommen komen uit een script dat de eigenaar van de database zelf draait. Zolang dat niet is
 * gebeurd, heeft een rij de sleutels niet en worden ze nooit geschreven. Een kolom die niemand
 * aanraakt, wordt ook niet geschreven. */

import type { Rij } from './website';

export const PLAATS = 'place';
export const BREEDTE = 'latitude';
export const LENGTE = 'longitude';
export const KOLOMMEN = [PLAATS, BREEDTE, LENGTE] as const;
/** Het script in de site-repo (greenvision-site) dat de kolommen toevoegt. */
export const SCRIPT = 'db/voorstel/2026-10-09-projectkaart.sql';

type Graadkolom = typeof BREEDTE | typeof LENGTE;

export const GRENZEN: Record<Graadkolom, { min: number; max: number; naam: string }> = {
  latitude: { min: 50.7, max: 53.6, naam: 'breedtegraad' },
  longitude: { min: 3.3, max: 7.3, naam: 'lengtegraad' },
};

/** Heeft deze rij de drie kolommen? Een rij uit een database zonder het script heeft de sleutels niet. */
export const heeftPlaats = (rij: Rij | null | undefined): boolean => !!rij && KOLOMMEN.every((k) => k in rij);

export type Graad =
  | { soort: 'leeg' }
  | { soort: 'fout' }
  /** `afgerond`: er stonden meer dan twee cijfers achter de komma. */
  | { soort: 'getal'; getal: number; afgerond: boolean };

/** Leest een graad: een getal uit de database (52.27, of als tekst "52.27") of wat iemand typt
 *  ("52,27"). Afgerond op twee cijfers achter de komma, zoals de database het zou doen. Er wordt met
 *  hele honderdsten gerekend, zodat 52,275 netjes 52,28 wordt. */
export function leesGraad(waarde: unknown): Graad {
  if (waarde == null) return { soort: 'leeg' };
  if (typeof waarde !== 'number' && typeof waarde !== 'string') return { soort: 'fout' };
  const tekst = String(waarde).trim();
  if (tekst === '') return { soort: 'leeg' };
  const delen = /^(-?)(\d+)(?:[.,](\d+))?$/.exec(tekst);
  if (!delen) return { soort: 'fout' };
  const [, min, geheel, breuk = ''] = delen;
  let honderdsten = Number(geheel) * 100 + Number(`${breuk}00`.slice(0, 2));
  if (breuk.length > 2 && breuk[2] >= '5') honderdsten += 1;
  const getal = honderdsten === 0 ? 0 : (min ? -honderdsten : honderdsten) / 100;
  return { soort: 'getal', getal, afgerond: /[1-9]/.test(breuk.slice(2)) };
}

/** Een graad zoals hij op het scherm staat: met een komma en twee cijfers erachter. */
export const toonGraad = (getal: number): string => getal.toFixed(2).replace('.', ',');

/** Wat er in het invoervak staat: het getal uit de database met een komma, of wat iemand typt. */
export const graadInvoer = (waarde: unknown): string => (typeof waarde === 'number' ? toonGraad(waarde) : typeof waarde === 'string' ? waarde : '');

/** Zelfde graad? "52,27", "52.270" en 52.27 zijn hetzelfde; leeg en geen waarde ook. */
export function zelfdeGraad(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  const x = leesGraad(a);
  const y = leesGraad(b);
  if (x.soort === 'getal' && y.soort === 'getal') return x.getal === y.getal;
  return x.soort === 'leeg' && y.soort === 'leeg';
}

const naamVan = (waarde: unknown): string => (typeof waarde === 'string' ? waarde : '');

const binnen = (kolom: Graadkolom, getal: number): boolean => getal >= GRENZEN[kolom].min && getal <= GRENZEN[kolom].max;

function controleerGraad(kolom: Graadkolom, graad: Graad): string | null {
  const { min, max, naam } = GRENZEN[kolom];
  const Naam = naam[0].toUpperCase() + naam.slice(1);
  if (graad.soort === 'fout') return `${Naam}: dit is geen getal. Schrijf het zo: ${toonGraad(kolom === BREEDTE ? 52.27 : 4.46)}.`;
  if (graad.soort !== 'getal' || binnen(kolom, graad.getal)) return null;
  const andere: Graadkolom = kolom === BREEDTE ? LENGTE : BREEDTE;
  const gewisseld = binnen(andere, graad.getal) ? ' Staan breedte en lengte omgekeerd?' : '';
  return `${Naam}: ${toonGraad(graad.getal)} ligt buiten Nederland. De ${naam} ligt tussen ${toonGraad(min)} en ${toonGraad(max)}.${gewisseld}`;
}

/** Wat er mis is, per kolom. Alleen als de breedte of de lengte is aangeraakt: wat er al stond, houdt
 *  het opslaan van een ander veld niet tegen. */
export function controleerPlaats(vorige: Rij, concept: Rij): Record<string, string> {
  const fouten: Record<string, string> = {};
  if (zelfdeGraad(vorige[BREEDTE], concept[BREEDTE]) && zelfdeGraad(vorige[LENGTE], concept[LENGTE])) return fouten;
  const breedte = leesGraad(concept[BREEDTE]);
  const lengte = leesGraad(concept[LENGTE]);
  for (const [kolom, graad] of [[BREEDTE, breedte], [LENGTE, lengte]] as const) {
    const fout = controleerGraad(kolom, graad);
    if (fout) fouten[kolom] = fout;
  }
  const samen = 'Vul de breedtegraad en de lengtegraad allebei in, of laat ze allebei leeg.';
  if (breedte.soort === 'leeg' && lengte.soort !== 'leeg') fouten[BREEDTE] = samen;
  if (lengte.soort === 'leeg' && breedte.soort !== 'leeg') fouten[LENGTE] = samen;
  return fouten;
}

/** De kolommen die geschreven moeten worden: alleen wat is aangeraakt en echt anders is. Niets als de
 *  rij de kolommen niet heeft (ook bij iets nieuws: dan is er nog geen rij) of als er iets niet klopt. */
export function schrijfPlaats(vorige: Rij | null | undefined, concept: Rij): Rij {
  const uit: Rij = {};
  if (!vorige || !heeftPlaats(vorige) || Object.keys(controleerPlaats(vorige, concept)).length > 0) return uit;
  if (PLAATS in concept && naamVan(concept[PLAATS]) !== naamVan(vorige[PLAATS])) {
    const naam = naamVan(concept[PLAATS]).trim();
    if (naam !== naamVan(vorige[PLAATS])) uit[PLAATS] = naam === '' ? null : naam;
  }
  for (const kolom of [BREEDTE, LENGTE] as const) {
    if (!(kolom in concept) || zelfdeGraad(vorige[kolom], concept[kolom])) continue;
    const graad = leesGraad(concept[kolom]);
    uit[kolom] = graad.soort === 'getal' ? graad.getal : null;
  }
  return uit;
}

export type StipStaat = 'compleet' | 'leeg' | 'geen_getallen' | 'geen_plaats' | 'niet_af';

/** Krijgt het project met deze invoer een stip? De site kijkt daarna zelf nog of het punt in
 *  Nederland ligt (op het land, of vlak bij de kust). */
export function stipStaat(concept: Rij): StipStaat {
  const naam = naamVan(concept[PLAATS]).trim();
  const breedte = leesGraad(concept[BREEDTE]);
  const lengte = leesGraad(concept[LENGTE]);
  const goed = breedte.soort === 'getal' && lengte.soort === 'getal' && binnen(BREEDTE, breedte.getal) && binnen(LENGTE, lengte.getal);
  if (naam && goed) return 'compleet';
  if (breedte.soort === 'leeg' && lengte.soort === 'leeg') return naam ? 'geen_getallen' : 'leeg';
  return goed ? 'geen_plaats' : 'niet_af';
}

export const STIP_ZIN: Record<StipStaat, string> = {
  compleet: 'Compleet: dit project krijgt een stip op de kaart.',
  leeg: 'Niets ingevuld: dit project staat niet op de kaart.',
  geen_getallen: 'Er staat een plaats zonder getallen: dit project staat nog niet op de kaart.',
  geen_plaats: 'Er staan getallen zonder plaats: dit project staat nog niet op de kaart.',
  niet_af: 'Nog niet af: dit project staat nog niet op de kaart.',
};
